import { CompanyAdminLayout } from "../../components/CompanyAdmin/CompanyAdminLayout";
import CircularProgress from "../../components/UI/CircularProgress";
import React, { type MouseEvent, useEffect, useRef, useState } from "react";
import {
  LucideBell,
  LucideFileLineChart,
  LucideFilter,
  LucidePlus,
  LucideTriangle,
  LucideTriangleAlert,
  LucideX,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../../components/UI/Button";
import { useParams } from "react-router-dom";
import type {
  MachineDetailDto,
  RouterMachinesResponse,
} from "../../../domain/types/machine";
import type { RouterInfoResponse } from "../../../domain/types/router";
import { companyMachineUseCase } from "../../../di/container";

const notificationMockJson = `[]`;

const formatRuntime = (runtimeSeconds: number | null): string => {
  if (runtimeSeconds === null || runtimeSeconds === 0) {
    return "0시간 0분";
  }

  const hours = Math.floor(runtimeSeconds / 3600);
  const minutes = Math.floor((runtimeSeconds % 3600) / 60);

  return `${hours}시간 ${minutes}분`;
};

interface MachineNotification {
  id: string;
  machineId: number;
  machineName: string;
  timestamp: string;
  type: "info" | "warning" | "error";
  title: string;
  description: string;
}

/**
 * 장비 이미지가 없을 때 쓰는 기본 그림.
 *
 * `printers/` 폴더에는 모델별 이미지만 있고 기본 이미지가 없어서, 예전에는 폴백 경로마저
 * 404 가 나며 alt 텍스트가 그대로 노출됐다.
 *
 * 플로터 그림을 쓴다. 여기 걸리는 장비는 재단기처럼 사진이 없는 업무용 기기라,
 * 가정용 프린터 그림보다 업무용 장비 실루엣이 오해가 적다.
 */
const DEFAULT_MACHINE_IMAGE = "/assets/images/plotter.png";

const NewEquipmentPage = () => {
  const { companyId } = useParams();

  const [notifications, setNotifications] = useState<
    MachineNotification[] | null
  >(null);

  useEffect(() => {
    const existingLocalStorage = localStorage.getItem("notifications");
    if (existingLocalStorage) {
      setNotifications(JSON.parse(existingLocalStorage));
    } else {
      setNotifications(JSON.parse(notificationMockJson));
    }
  }, []);

  useEffect(() => {
    if (notifications) {
      localStorage.setItem("notifications", JSON.stringify(notifications));
    }
  }, [notifications]);

  const [selectedRouterId, setSelectedRouterId] = useState<"all" | number>(
    "all",
  );
  const [selectedMachineFilter, setSelectedMachineFilter] = useState<
    null | number
  >(null);

  const [machineDetailsByRouter, setMachineDetailsByRouter] =
    useState<RouterMachinesResponse[]>();
  const [routers, setRouters] = useState<RouterInfoResponse[]>([]);
  const [machines, setMachines] = useState<MachineDetailDto[]>([]);

  useEffect(() => {
    setRouters(machineDetailsByRouter?.flatMap((item) => item.router) ?? []);
  }, [machineDetailsByRouter]);
  useEffect(() => {
    const allMachines =
      machineDetailsByRouter?.flatMap((item) => item.machines) ?? [];
    setMachines(allMachines);

    const printerNames = allMachines.map((machine) => machine.modelName);
    findMatchingImageNames(printerNames);
  }, [machineDetailsByRouter]);

  useEffect(() => {
    (async () => {
      if (companyId) {
        const fetchedMachineDetailsByRouter =
          await companyMachineUseCase.getMachineDetailsByRouter(
            Number(companyId),
          );
        console.log(fetchedMachineDetailsByRouter);
        setMachineDetailsByRouter(fetchedMachineDetailsByRouter);
      }
    })();
  }, [companyId]);

  const equipmentBlockRef = useRef<HTMLDivElement>(null);
  const [isEquipmentElementRowFlex, setIsEquipmentElementRowFlex] =
    useState(true);

  const [isSidebarMenuOpen, setIsSidebarMenuOpen] = useState(false);

  const handleResize = () => {
    if (!equipmentBlockRef.current) return;

    if (isSidebarMenuOpen) {
      if (equipmentBlockRef.current.clientWidth < 900) {
        setIsEquipmentElementRowFlex(false);
      } else {
        setIsEquipmentElementRowFlex(true);
      }
    } else {
      if (equipmentBlockRef.current.clientWidth < 870) {
        setIsEquipmentElementRowFlex(false);
      } else {
        setIsEquipmentElementRowFlex(true);
      }
    }
  };

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    handleResize();
  }, [handleResize, isSidebarMenuOpen]);

  const filterIconClickHandler = (index: number) => {
    setSelectedMachineFilter(index);
    setIsSidebarMenuOpen(true);
  };

  const [imageNames, setImageNames] = useState<string[]>([]);

  const findMatchingImageNames = (printerNames: string[]) => {
    const getImageFileNames = () => {
      return [
        "AccurioPress 7136",
        "DocuCentre C5500",
        "Duplo DC-618",
        "FUJI XEROX 4112",
        "FUJI XEROX ApeosPort-IV C5570",
        "FUJI XEROX ApeosPort-V C5580 T2",
        "FUJI XEROX ApeosPort-VII",
        "FUJI XEROX D125",
        "FUJI XEROX D136",
        "FUJI XEROX DocuCentre-IV",
        "FUJI XEROX DocuPrint C2255",
        "FUJI XEROX DocuPrint C5005",
        "FUJI XEROX DocuPrint C5155 d",
        "FUJIFILM Revoria Press E1136",
        "Fuji Xerox DocuPrint C5155",
        "Fuji Xerox DocuPrint C5155_2",
        "Fuji Xerox DocuPrint C5155_3",
        "KONICA MINOLTA AccurioPress 6136",
        "KONICA MINOLTA AccurioPress C14000",
        "KONICA MINOLTA AccurioPress C14000_2",
        "KONICA MINOLTA AccurioPress C3080",
        "KONICA MINOLTA AccurioPress C4080 IC-609",
        "KONICA MINOLTA AccurioPress C4080",
        "KONICA MINOLTA AccurioPrint C4065",
        "KONICA MINOLTA bizhub C308",
        "KONICA MINOLTA bizhub PRESS C1070",
        "Konica Minolta bizhub PRESS 1250",
        "RICOH Pro 8220S",
        "RICOH Pro C5200S",
        "RICOH Pro C5310S",
        "RICOH Pro C7500",
        "RICOH Pro C7500_2",
        "RICOH Pro C7500_3",
        "RICOH Pro C9210",
        "Revoria Press SC285S",
        "SINDOH N600",
        "Xerox D136",
        "Xerox Versant 180 Press",
        "Xerox Versant 80 Press",
      ];
    };

    let newArray: string[] = [];

    const printerImageFileNames = getImageFileNames();
    for (const printerName of printerNames) {
      let bestMatch = "";
      let bestScore = 0;

      for (const imageFileName of printerImageFileNames) {
        let editingImageFileName = imageFileName;
        editingImageFileName = editingImageFileName.replaceAll(" ", " ");
        editingImageFileName = editingImageFileName.replaceAll("-", " ");
        editingImageFileName = editingImageFileName.replaceAll("_", " ");
        editingImageFileName = editingImageFileName.toLowerCase();

        const score = getMatchScore(printerName, imageFileName);
        if (score > bestScore) {
          bestScore = score;
          bestMatch = imageFileName;
        }
      }

      newArray.push(bestMatch || "printer");
    }

    setImageNames(newArray);
  };
  useEffect(() => {
    if (machineDetailsByRouter && machineDetailsByRouter.length > 0) {
      const newPrinterNames: string[] = [];
      for (const item_i of machineDetailsByRouter) {
        for (const item_j of item_i.machines) {
          newPrinterNames.push(item_j.modelName);
        }
      }
      findMatchingImageNames(newPrinterNames);
    }
  }, [machineDetailsByRouter, machines]);

  const getMatchScore = (
    printerName: string,
    imageFileName: string,
  ): number => {
    const printerWords = printerName.toLowerCase().split(" ");
    const imageWords = imageFileName.toLowerCase().split(" ");

    let score = 0;

    for (const printerWord of printerWords) {
      for (const imageWord of imageWords) {
        if (printerWord === imageWord) {
          if (printerWord === "AccurioPress") {
            score += 10;
          } else {
            score += 30;
          }
        } else {
          if (
            printerWord.includes(imageWord) &&
            imageWord.includes(printerWord)
          ) {
            score += 5;
          }
        }
      }
    }

    return score;
  };

  return (
    <>
      <CompanyAdminLayout>
        <div className="p-5 relative">
          <div className="relative mb-[10px] / flex flex-col items-start gap-[0.5rem]">
            <h2 className="text-[1.375rem] / font-semibold">장비 관리</h2>
            <span className="text-[1rem] font-normal">
              총 {machines.length}개의 장비가 있습니다.
            </span>
          </div>
          <div className="mb-8 w-full overflow-x-scroll p-[4px]">
            <div className="flex gap-2 h-11 text-nowrap">
              <Button
                onClick={() => {
                  setSelectedRouterId("all");
                }}
                variant={selectedRouterId === "all" ? "primary" : "outline"}
                className="px-6"
              >
                전체
              </Button>
              {routers.map((item) => {
                return (
                  <>
                    <Button
                      variant={
                        selectedRouterId === item.id ? "primary" : "outline"
                      }
                      onClick={() => item.id && setSelectedRouterId(item.id)}
                    >
                      <div>{item.routerName}</div>
                    </Button>
                  </>
                );
              })}
            </div>
          </div>

          <div className="flex flex-row">
            <div className="relative flex flex-col gap-[25px] w-full">
              <div className="z-[10px] absolute right-0 -top-[155px]">
                <button
                  onClick={() => {
                    setIsSidebarMenuOpen(!isSidebarMenuOpen);
                  }}
                >
                  {/*<div className="absolute right-0 top-0 bg-red-400 w-[10px] h-[10px] rounded-full"></div>*/}

                  <LucideBell className="" />
                </button>
              </div>
              {machineDetailsByRouter?.map((item, index_i) => {
                const readingRouter = item.router;

                if (
                  selectedRouterId !== "all" &&
                  selectedRouterId !== readingRouter.id
                ) {
                  return null;
                }

                return item.machines.map((machine, index_j) => {
                  const paperLevel =
                    machine.trays.length > 0
                      ? (machine.trays[0].percentage ?? 0)
                      : 0;
                  const isPaperLow = paperLevel < 20;

                  // Calculate global machine index across all routers
                  let imageIndex = 0;
                  for (let i = 0; i < index_i; i++) {
                    imageIndex += machineDetailsByRouter[i].machines.length;
                  }
                  imageIndex += index_j;
                  return (
                    <>
                      <div
                        ref={equipmentBlockRef}
                        data-index={0}
                        className={`relative p-10 gap-[25px] rounded-xl items-center justify-center text-start flex ${isEquipmentElementRowFlex ? "flex-row" : "flex-col"} w-full bg-[#F9FAFB] outline outline-[#E8E8E8] outline-1`}
                      >
                        <button
                          onClick={() => {
                            filterIconClickHandler(index_j);
                          }}
                        >
                          <div className="bg-[#D9D9D9] rounded-full p-[6px] absolute bottom-6 right-6">
                            <LucideFilter />
                          </div>
                        </button>

                        <div
                          className={`max-w-[400px] items-center flex ${isSidebarMenuOpen ? "flex-col lg:flex-row" : "flex-col sm:flex-row"} gap-[30px]`}
                        >
                          <img
                            className="min-w-[150px] w-[150px] object-contain"
                            src={
                              imageNames[imageIndex]
                                ? `/assets/images/printers/${imageNames[imageIndex]}.png`
                                : DEFAULT_MACHINE_IMAGE
                            }
                            alt={machine.displayName ?? machine.modelName}
                            onError={(e) => {
                              // 모델 이미지가 등록돼 있어도 파일이 없거나 이름이 어긋날 수 있다.
                              // 그때 깨진 이미지 대신 기본 프린터 그림을 보여준다.
                              const img = e.currentTarget;
                              if (img.src.endsWith(DEFAULT_MACHINE_IMAGE))
                                return;
                              img.src = DEFAULT_MACHINE_IMAGE;
                            }}
                          />

                          <div className="flex flex-col justify-center gap-[17px] md:min-w-[200px]">
                            <div>
                              <h2 className="font-bold text-[20px]">
                                {machine.displayName ?? machine.modelName}
                              </h2>
                              <div>{`PODRUT-${machine.machineId}`}</div>
                            </div>
                            <div className="flex gap-[15px] text-nowrap">
                              <div className="text-[12px]">
                                <div className="text-[#8D8D8D]">IP주소</div>
                                <div>{machine.ipAddress}</div>
                              </div>
                              <div className="text-[12px]">
                                <div className="text-[#8D8D8D]">가동시간</div>
                                <div>
                                  {machine.runtime &&
                                    formatRuntime(machine.runtime)}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div
                          className={`${isEquipmentElementRowFlex ? "hidden" : "w-full h-[2px] my-4"} bg-[#D9D9D9]`}
                        ></div>

                        <div
                          className={`flex flex-col ${isSidebarMenuOpen ? "lg:flex-row" : "sm:flex-row"} lg:flex-row gap-[1rem]`}
                        >
                          <div
                            className={`relative w-full bg-[#F9FAFB] rounded-[0.625rem]
                  / flex flex-row items-center`}
                          >
                            <div className="mr-[0.75rem]">
                              <CircularProgress
                                size={70}
                                fontsize={18}
                                strokeWidth={7}
                                strokeColor={isPaperLow ? "#FF4D4F" : "#4285F4"}
                                value={paperLevel}
                              />
                            </div>
                            <div className="text-start">
                              <div className="mb-[0.28125rem] text-[0.6875rem]">
                                종이
                              </div>
                              <div className="text-[0.875rem]">Paper</div>
                            </div>
                          </div>
                          <div className="w-full bg-[#F9FAFB] rounded-[0.625rem] p-[1rem]">
                            <div className="grid grid-cols-2 gap-x-[25px] gap-y-[15px] / items-center">
                              <div className="flex flex-row justify-start items-center gap-[0.5rem]">
                                <div
                                  className="min-w-[1.5rem] h-[1.5rem] bg-[#00A0E9] text-white text-[0.625rem]
                       rounded-[0.25rem] flex justify-center items-center"
                                >
                                  C
                                </div>
                                <span className="text-[0.6875rem] flex items-center">
                                  <span className="font-bold text-[0.9375rem]">
                                    {machine.supplies.cyan?.percentage ?? 0}
                                  </span>
                                  %
                                </span>
                              </div>
                              <div className="flex flex-row justify-start items-center gap-[0.5rem]">
                                <div
                                  className="min-w-[1.5rem] h-[1.5rem] bg-[#E4007F] text-white text-[0.625rem]
                       rounded-[0.25rem] flex justify-center items-center"
                                >
                                  M
                                </div>
                                <span className="text-[0.6875rem] flex items-center">
                                  <span className="font-bold text-[0.9375rem]">
                                    {machine.supplies.magenta?.percentage ?? 0}
                                  </span>
                                  %
                                </span>
                              </div>
                              <div className="flex flex-row justify-start items-center gap-[0.5rem]">
                                <div
                                  className="min-w-[1.5rem] h-[1.5rem] bg-[#FFA100] text-white text-[0.625rem]
                       rounded-[0.25rem] flex justify-center items-center"
                                >
                                  Y
                                </div>
                                <span className="text-[0.6875rem] flex items-center">
                                  <span className="font-bold text-[0.9375rem]">
                                    {machine.supplies.yellow?.percentage ?? 0}
                                  </span>
                                  %
                                </span>
                              </div>
                              <div className="flex flex-row justify-start items-center gap-[0.5rem]">
                                <div
                                  className="min-w-[1.5rem] h-[1.5rem] bg-[#3E3E3E] text-white text-[0.625rem]
                       rounded-[0.25rem] flex justify-center items-center"
                                >
                                  K
                                </div>
                                <span className="text-[0.6875rem] flex items-center">
                                  <span className="font-bold text-[0.9375rem]">
                                    {machine.supplies.black?.percentage ?? 0}
                                  </span>
                                  %
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  );
                });
              })}
            </div>
            <AnimatePresence>
              {isSidebarMenuOpen && (
                <motion.div
                  initial={{ x: "100%" }}
                  animate={{ x: 0 }}
                  exit={{ x: "100%" }}
                  transition={{ duration: 0.2, ease: "circInOut" }}
                  className="pl-4 md:w-full md:max-w-[350px]"
                ></motion.div>
              )}
            </AnimatePresence>
            <AnimatePresence>
              {isSidebarMenuOpen && (
                <motion.div
                  initial={{ x: "100%" }}
                  animate={{ x: 0 }}
                  exit={{ x: "100%" }}
                  transition={{ duration: 0.2, ease: "circInOut" }}
                  className="fixed w-[350px] top-[55px] right-0 bottom-0 bg-[#E8E8E8] flex flex-col"
                >
                  <div className="w-full p-5 flex justify-between">
                    <button onClick={() => setIsSidebarMenuOpen(false)}>
                      <LucidePlus className="rotate-45" />
                    </button>
                    <button
                      onClick={() => {
                        setNotifications([]);
                      }}
                      className="underline text-[#4285F4]"
                    >
                      전체 알림 지우기
                    </button>
                  </div>
                  {selectedMachineFilter !== null && (
                    <div className="text-start px-5 text-[14px] mb-4 flex items-center">
                      <div>
                        <b>필터: </b>
                        <span>
                          {machines[selectedMachineFilter]?.displayName ??
                            machines[selectedMachineFilter]?.modelName}
                        </span>
                      </div>
                      <button
                        onClick={() => {
                          setSelectedMachineFilter(null);
                        }}
                        className="ml-2 bg-[#AEAEAE] rounded-full aspect-square flex justify-center items-center scale-[0.9]"
                      >
                        <LucideX className="scale-[0.6] text-white" />
                      </button>
                    </div>
                  )}

                  <div className="overflow-y-scroll flex-1">
                    <div className="px-5 flex-col flex gap-3 mb-4">
                      {notifications
                        ?.filter(
                          (item) =>
                            selectedMachineFilter === null ||
                            item.machineId ===
                              machines[selectedMachineFilter]?.machineId,
                        )
                        .map((item) => (
                          <div className="flex gap-[16px] bg-white rounded-2xl text-start p-4">
                            {item.type === "info" ? (
                              <LucideFileLineChart className="min-w-[30px] scale-[0.8] text-[#252525]" />
                            ) : null}
                            {item.type === "warning" ? (
                              <LucideTriangleAlert className="min-w-[30px] scale-[0.8] text-[#FFA100]" />
                            ) : null}
                            {item.type === "error" ? (
                              <LucideTriangleAlert className="min-w-[30px] scale-[0.8] text-[#FF0000]" />
                            ) : null}
                            <div>
                              <div className="mb-1">
                                <h3 className="text-[#252525] text-[14px]">
                                  {item.machineName}
                                </h3>
                                <div className="text-[12px]">
                                  {item.timestamp}
                                </div>
                              </div>
                              <h2 className="font-bold text-[20px] mb-2">
                                {item.title}
                              </h2>
                              <div className="text-[14px]">
                                {item.description}
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </CompanyAdminLayout>
    </>
  );
};

export default NewEquipmentPage;
